import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pencil, ChartSpline, GitCompare, BookOpen, ThumbsUp, SendHorizontal } from 'lucide-react';
import chatRobot from '@/imgs/chat-robot.png';
import arrowDownLinear from '@/imgs/arrow-down-linear.svg';
import { useChatContext } from '@/contexts/ChatContext';

const ChatSection: React.FC<{ scrollDown: () => void; setActiveTab: (tab: number) => void }> = ({
  scrollDown,
  setActiveTab
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isChatOpen, setIsChatOpen] = useState(false);

  const {
    selectedButton,
    isProcessing,
    setSelectedButton,
    processMessage,
    addMessage,
    clearMessages
  } = useChatContext();

  const handleClick = (url: string, buttonType: string) => {
    setSelectedButton(buttonType);
    // window.location.href = url;
    console.log(url);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isProcessing) return;

    const message = inputValue.trim();
    addMessage({
      position: 'right',
      content: message
    });
    setInputValue('');
    setActiveTab(1);

    addMessage({
      position: 'left',
      content: message,
      loading: true
    });

    // 调用 Charts 组件中的接口处理逻辑
    await processMessage(message, selectedButton || 'advisory');
  };

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
    scrollDown();
  };

  const handleNewChat = () => {
    clearMessages();
    setInputValue('');
    setActiveTab(1);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 h-[200px] rounded-t-lg border-l border-[#F0F0F0] bg-white px-4 py-7 shadow-[20px_-4px_20px_0_rgba(89,89,235,0.09)]">
      {/* 聊天区域 */}
      <div>
        <div className="relative h-8 w-full">
          <div
            className="absolute right-0 top-0 h-[52px] w-[52px] cursor-pointer rounded-full bg-[linear-gradient(270deg,#3215F3_0%,#FF4876_100%)] p-[1px]"
            onClick={handleNewChat}
          >
            <img src={chatRobot} alt="" width={52} height={52} />
          </div>
          <div
            className="absolute left-[calc(50%-15px)] top-[calc(50%-15px)] flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.06)]"
            onClick={toggleChat}
          >
            <img src={arrowDownLinear} width={20} height={20} />
          </div>
        </div>

        {/* 快捷功能按钮 */}
        <div className="my-4 flex justify-start gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={isProcessing}
            className={`flex h-auto items-center gap-1 rounded-md px-2 py-1 text-xs transition-all duration-200 ${
              selectedButton === 'advisory'
                ? 'border-[#5959EB] bg-[#5959EB] text-white shadow-md'
                : 'border-[#F0F0F0] text-[#595959] hover:bg-gray-50'
            } ${isProcessing ? 'cursor-not-allowed opacity-50' : ''}`}
            onClick={() => {
              handleClick('/aiAdvisorDiagnosis', 'advisory');
            }}
          >
            <Pencil className="h-4 w-4" />
            投顾文案
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={isProcessing}
            className={`flex h-auto items-center gap-1 rounded-md px-2 py-1 text-xs transition-all duration-200 ${
              selectedButton === 'market'
                ? 'border-[#5959EB] bg-[#5959EB] text-white shadow-md'
                : 'border-[#F0F0F0] text-[#595959] hover:bg-gray-50'
            } ${isProcessing ? 'cursor-not-allowed opacity-50' : ''}`}
            onClick={() => {
              handleClick('/marketAnalysis', 'market');
            }}
          >
            <ChartSpline className="h-4 w-4" />
            市场解读
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={isProcessing}
            className={`flex h-auto items-center gap-1 rounded-md px-2 py-1 text-xs transition-all duration-200 ${
              selectedButton === 'compare'
                ? 'border-[#5959EB] bg-[#5959EB] text-white shadow-md'
                : 'border-[#F0F0F0] text-[#595959] hover:bg-gray-50'
            } ${isProcessing ? 'cursor-not-allowed opacity-50' : ''}`}
            onClick={() => {
              handleClick('/productCompare', 'compare');
            }}
          >
            <GitCompare className="h-4 w-4" />
            产品对比
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={isProcessing}
            className={`flex h-auto items-center gap-1 rounded-md px-2 py-1 text-xs transition-all duration-200 ${
              selectedButton === 'interpretation'
                ? 'border-[#5959EB] bg-[#5959EB] text-white shadow-md'
                : 'border-[#F0F0F0] text-[#595959] hover:bg-gray-50'
            } ${isProcessing ? 'cursor-not-allowed opacity-50' : ''}`}
            onClick={() => {
              handleClick('/productInterpretation', 'interpretation');
            }}
          >
            <BookOpen className="h-4 w-4" />
            产品解读
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={isProcessing}
            className={`flex h-auto items-center gap-1 rounded-md px-2 py-1 text-xs transition-all duration-200 ${
              selectedButton === 'recommendation'
                ? 'border-[#5959EB] bg-[#5959EB] text-white shadow-md'
                : 'border-[#F0F0F0] text-[#595959] hover:bg-gray-50'
            } ${isProcessing ? 'cursor-not-allowed opacity-50' : ''}`}
            onClick={() => {
              handleClick('/productRecommendation', 'recommendation');
            }}
          >
            <ThumbsUp className="h-4 w-4" />
            产品推荐
          </Button>
        </div>

        {/* 聊天输入框 */}
        <div className="border-gradient-to-r relative mb-4 rounded-lg border bg-white from-[#3215F3] to-[#ECB1FF]">
          {/* 渐变边框效果 */}
          <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-[#3215F3] to-[#ECB1FF] p-[1px]">
            <div className="h-full w-full rounded-lg bg-white"></div>
          </div>

          <div className="relative flex items-center justify-between gap-2.5 px-3 py-2">
            <Input
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && inputValue.trim() && !isProcessing) {
                  handleSendMessage();
                }
              }}
              placeholder={isProcessing ? '处理中...' : '请帮我生成产品对比~'}
              disabled={isProcessing}
              className={`flex-1 border-0 bg-transparent text-sm text-[#8C8C8C] placeholder:text-[#8C8C8C] focus-visible:ring-0 focus-visible:ring-offset-0 ${
                isProcessing ? 'cursor-not-allowed opacity-50' : ''
              }`}
            />
            <Button
              size="sm"
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isProcessing}
              className={`h-6 w-6 rounded p-0 ${
                inputValue.trim() && !isProcessing
                  ? 'bg-gradient-to-r from-[#3215F3] to-[#ECB1FF] hover:from-[#2A13E0] hover:to-[#D49AFF]'
                  : 'bg-[#D9D9D9] hover:bg-[#CCCCCC]'
              }`}
            >
              <SendHorizontal className="h-4 w-4 text-white" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatSection;
